import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const COL_READY      = '#C8922A';
const COL_DISPATCHED = '#4ade80';
const COL_PENDING    = '#f87171';
const COL_RECONCILED = '#a78bfa';
const COL_TO_MOVE    = '#60a5fa';
const COL_MOVED      = '#a78bfa';

const EVENT_COLORS = {
  'First Run':    'var(--muted)',
  'Snapshot':     'var(--muted)',
  'PO Received':  '#60a5fa',
  'Inbound Sale': '#f59e0b',
  'Reconciled':   COL_RECONCILED,
};

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: false });
};

// Sort: excluded last, then pending before dispatched, then by first_ready_nzt
const sortOrders = (list) => list.sort((a, b) => {
  if (a.chart_excluded !== b.chart_excluded) return a.chart_excluded ? 1 : -1;
  if (!!a.dispatched_nzt !== !!b.dispatched_nzt) return a.dispatched_nzt ? 1 : -1;
  return new Date(a.first_ready_nzt) - new Date(b.first_ready_nzt);
});

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NZ', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// ── Expandable order row ───────────────────────────────────────────────────────
const OrderRow = ({ order }) => {
  const [expanded, setExpanded] = useState(false);
  const isDispatched   = !!order.dispatched_nzt;
  const isExcludedRow  = !!order.chart_excluded;
  const statusColor    = isDispatched ? COL_DISPATCHED : COL_PENDING;
  const statusLabel    = isDispatched ? 'Dispatched' : 'Pending';
  const eventColor     = EVENT_COLORS[order.event_type] || 'var(--muted)';
  const rowOpacity     = isExcludedRow ? 0.5 : 1;
  const hasMoveData    = (order.lines || []).some(l => l.can_move != null);

  return (
    <>
      {/* Summary row */}
      <tr
        onClick={() => setExpanded(e => !e)}
        style={{
          cursor: 'pointer',
          borderBottom: expanded ? 'none' : '1px solid var(--border)',
          background: expanded ? 'rgba(255,255,255,0.03)' : 'transparent',
          transition: 'background 0.15s',
          opacity: rowOpacity,
        }}
      >
        {/* Expand toggle */}
        <td style={{ padding: '10px 8px 10px 16px', width: 28 }}>
          <span style={{
            display: 'inline-block',
            fontSize: 10,
            color: 'var(--muted)',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}>▶</span>
        </td>

        {/* Order ref */}
        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600, color: 'var(--white)', whiteSpace: 'nowrap' }}>
          {order.order_ref}
        </td>

        {/* Stage */}
        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--muted)' }}>
          {order.stage || '—'}
        </td>

        {/* First ready */}
        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--white)', whiteSpace: 'nowrap' }}>
          {fmt(order.first_ready_nzt)}
        </td>

        {/* Dispatched time */}
        <td style={{ padding: '10px 12px', fontSize: 12, color: isDispatched ? COL_DISPATCHED : 'var(--muted)', whiteSpace: 'nowrap' }}>
          {fmt(order.dispatched_nzt)}
        </td>

        {/* Status */}
        <td style={{ padding: '10px 12px' }}>
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: statusColor,
            background: `${statusColor}18`,
            border: `1px solid ${statusColor}40`,
            borderRadius: 4,
            padding: '2px 8px',
            whiteSpace: 'nowrap',
          }}>
            {statusLabel}
          </span>
          {order.backorder_refs && (
            <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 8, whiteSpace: 'nowrap' }}>
              Backorder: {order.backorder_refs}
            </span>
          )}
          {order.to_move_at && (
            <span style={{
              fontSize: 11,
              color: order.moved_at ? COL_MOVED : COL_TO_MOVE,
              marginLeft: 8,
              whiteSpace: 'nowrap',
            }}>
              {order.moved_at
                ? `Moved ${fmt(order.moved_at)}`
                : `Items to move since ${fmt(order.to_move_at)}`}
            </span>
          )}
        </td>

        {/* Event type */}
        <td style={{ padding: '10px 12px' }}>
          <span style={{
            fontSize: 11,
            color: eventColor,
            background: `${eventColor}15`,
            border: `1px solid ${eventColor}30`,
            borderRadius: 4,
            padding: '2px 8px',
            whiteSpace: 'nowrap',
          }}>
            {order.event_type || '—'}
          </span>
        </td>

        {/* Chart included/excluded */}
        <td style={{ padding: '10px 16px 10px 12px' }}>
          {isExcludedRow ? (
            <span style={{
              fontSize: 11,
              color: COL_PENDING,
              background: `${COL_PENDING}12`,
              border: `1px solid ${COL_PENDING}30`,
              borderRadius: 4,
              padding: '2px 8px',
              whiteSpace: 'nowrap',
            }}>
              ✗ Excluded
            </span>
          ) : (
            <span style={{
              fontSize: 11,
              color: COL_DISPATCHED,
              background: `${COL_DISPATCHED}12`,
              border: `1px solid ${COL_DISPATCHED}30`,
              borderRadius: 4,
              padding: '2px 8px',
              whiteSpace: 'nowrap',
            }}>
              ✓ Included
            </span>
          )}
        </td>
      </tr>

      {/* Expanded detail rows */}
      {expanded && (
        <tr style={{ borderBottom: '1px solid var(--border)' }}>
          <td colSpan={8} style={{ padding: '0 16px 12px 44px' }}>
            <div style={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              overflow: 'hidden',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Item Code', 'Qty Ordered', 'Qty Available', 'Line Status',
                      ...(hasMoveData ? ['On Hand', 'Qty Moved', 'Can Move'] : [])].map(h => (
                      <th key={h} style={{
                        padding: '7px 12px',
                        textAlign: 'left',
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(order.lines || []).map((line, i) => {
                    const lineReady = line.dispatch_status === 'Ready';
                    return (
                      <tr key={i} style={{ borderBottom: i < order.lines.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <td style={{ padding: '7px 12px', color: 'var(--white)', fontFamily: 'monospace' }}>{line.item_code}</td>
                        <td style={{ padding: '7px 12px', color: 'var(--muted)' }}>{line.qty_ordered}</td>
                        <td style={{ padding: '7px 12px', color: lineReady ? COL_DISPATCHED : COL_PENDING }}>{line.qty_available}</td>
                        <td style={{ padding: '7px 12px' }}>
                          <span style={{
                            fontSize: 10,
                            color: lineReady ? COL_DISPATCHED : COL_PENDING,
                          }}>
                            {lineReady ? '✓ Ready' : '✗ Not Ready'}
                          </span>
                        </td>
                        {hasMoveData && (
                          <>
                            <td style={{ padding: '7px 12px', color: 'var(--muted)' }}>{line.qty_on_hand ?? '—'}</td>
                            <td style={{ padding: '7px 12px', color: 'var(--muted)' }}>{line.qty_moved ?? '—'}</td>
                            <td style={{ padding: '7px 12px', fontSize: 10, color: line.can_move ? COL_TO_MOVE : 'var(--muted)' }}>
                              {line.can_move ? '→ Move now' : '—'}
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                  {(!order.lines || order.lines.length === 0) && (
                    <tr>
                      <td colSpan={hasMoveData ? 7 : 4} style={{ padding: '10px 12px', color: 'var(--muted)', fontStyle: 'italic' }}>
                        No line detail available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────
const DispatchDrilldown = () => {
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();
  const isPartial       = searchParams.get('mode') === 'partial';

  const todayStr = () => new Date().toLocaleDateString('en-CA');
  const [date, setDate]           = useState(searchParams.get('date') || todayStr());
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [filter, setFilter]       = useState('all'); // all | dispatched | pending
  const [search, setSearch]       = useState('');

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      // Server-side: NZ day bounds via Pacific/Auckland, and no PostgREST row cap on raw snapshot rows
      const rpcName = isPartial ? 'get_partial_dispatch_drilldown' : 'get_dispatch_drilldown';
      const { data, error: err } = await supabase.rpc(rpcName, { p_date: d });
      if (err) throw err;
      setOrders(sortOrders((data || []).map(r => ({
        order_id:        r.order_id,
        order_ref:       r.order_ref,
        stage:           r.stage,
        first_ready_nzt: r.first_ready_at,
        event_type:      r.event_type,
        dispatched_nzt:  r.dispatched_date,
        backorder_refs:  r.backorder_refs,
        to_move_at:      r.to_move_at,
        moved_at:        r.moved_at,
        chart_excluded:  r.chart_excluded,
        lines:           r.lines || [],
      }))));
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message || 'Failed to load data');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [isPartial]);

  useEffect(() => { load(date); }, [date, load]);

  // Derived — only count included orders in KPIs
  const includedOrders = orders.filter(o => !o.chart_excluded);
  const excludedOrders = orders.filter(o =>  o.chart_excluded);
  const totalReady     = includedOrders.length;
  const dispatched     = includedOrders.filter(o => !!o.dispatched_nzt).length;
  const pending        = totalReady - dispatched;
  const rate           = totalReady > 0 ? Math.round((dispatched / totalReady) * 100) : 0;
  const backordered    = includedOrders.filter(o => !!o.dispatched_nzt && !!o.backorder_refs).length;
  const toMove         = includedOrders.filter(o => !!o.to_move_at).length;
  const moved          = includedOrders.filter(o => !!o.to_move_at && !!o.moved_at).length;

  const filtered = orders.filter(o => {
    if (filter === 'dispatched' && (o.chart_excluded || !o.dispatched_nzt)) return false;
    if (filter === 'pending'    && (o.chart_excluded ||  o.dispatched_nzt)) return false;
    if (filter === 'excluded'   && !o.chart_excluded) return false;
    if (filter === 'to_move'    && (o.chart_excluded || !o.to_move_at || o.moved_at)) return false;
    if (search && !o.order_ref.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <main className="app-main">
      <div style={{ padding: '24px 32px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: 'var(--muted)',
              fontSize: 13,
              padding: '6px 12px',
              cursor: 'pointer',
            }}
          >
            ← Back
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--white)', margin: 0 }}>
              📬 {isPartial ? 'Partial Order Throughput' : 'Dispatch Throughput'} — Detail
            </h1>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
              {isPartial
                ? 'Orders with some lines ready and some not — lines shown as at first capture with items to move, else first partial capture'
                : 'Underlying order data for the dispatch chart'}
              {lastRefresh && ` · updated ${lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
            </div>
          </div>

          {/* Date picker + refresh */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              style={{
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--white)',
                fontSize: 12,
                padding: '5px 10px',
                cursor: 'pointer',
              }}
            />
            <button
              onClick={() => load(date)}
              disabled={loading}
              style={{
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: loading ? 'var(--muted)' : 'var(--white)',
                fontSize: 12,
                padding: '5px 12px',
                cursor: loading ? 'default' : 'pointer',
              }}
            >
              {loading ? '…' : '↻ Refresh'}
            </button>
          </div>
        </div>

        {/* KPI bar */}
        {!loading && !error && totalReady > 0 && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            {[
              { label: 'Ready',      value: totalReady, color: COL_READY },
              { label: 'Dispatched', value: dispatched,  color: COL_DISPATCHED },
              { label: 'Pending',    value: pending,     color: COL_PENDING },
              { label: 'Rate',       value: `${rate}%`,  color: rate >= 80 ? COL_DISPATCHED : rate >= 50 ? COL_READY : COL_PENDING },
              ...(isPartial ? [
                { label: 'Backordered',   value: backordered,     color: 'var(--muted)' },
                { label: 'Items to move', value: toMove,          color: COL_TO_MOVE },
                { label: 'Moved',         value: moved,           color: COL_MOVED },
                { label: 'Still to move', value: toMove - moved,  color: toMove - moved > 0 ? COL_PENDING : 'var(--muted)' },
              ] : []),
            ].map(({ label, value, color }) => (
              <div key={label} style={{
                background: 'var(--bg2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '10px 18px',
                minWidth: 80,
              }}>
                <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters + search */}
        {!loading && !error && totalReady > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { key: 'all',        label: `All (${orders.length})` },
              { key: 'pending',    label: `Pending (${pending})` },
              { key: 'dispatched', label: `Dispatched (${dispatched})` },
              ...(isPartial ? [{ key: 'to_move', label: `Still to move (${toMove - moved})` }] : []),
              { key: 'excluded',   label: `Excluded (${excludedOrders.length})` },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                style={{
                  background: filter === key ? 'var(--bg2)' : 'transparent',
                  border: `1px solid ${filter === key ? 'var(--border)' : 'transparent'}`,
                  borderRadius: 6,
                  color: filter === key ? 'var(--white)' : 'var(--muted)',
                  fontSize: 12,
                  padding: '5px 12px',
                  cursor: 'pointer',
                  fontWeight: filter === key ? 600 : 400,
                }}
              >
                {label}
              </button>
            ))}

            <input
              type="text"
              placeholder="Search order ref…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                marginLeft: 'auto',
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--white)',
                fontSize: 12,
                padding: '5px 12px',
                width: 180,
                outline: 'none',
              }}
            />
          </div>
        )}

        {/* States */}
        {loading && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
            Loading…
          </div>
        )}
        {!loading && error && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: COL_PENDING, fontSize: 13 }}>
            {error}
          </div>
        )}
        {!loading && !error && totalReady === 0 && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
            No dispatch data for {fmtDate(`${date}T00:00:00`)}.
          </div>
        )}

        {/* Table */}
        {!loading && !error && totalReady > 0 && (
          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,0.02)' }}>
                  <th style={{ width: 28 }} />
                  {['Order Ref', 'Stage', 'First Ready', 'Dispatched', 'Status', 'Event Type', 'Chart'].map(h => (
                    <th key={h} style={{
                      padding: '10px 12px',
                      textAlign: 'left',
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>
                      No orders match your filter.
                    </td>
                  </tr>
                ) : (
                  filtered.map(order => (
                    <OrderRow key={order.order_id} order={order} />
                  ))
                )}
              </tbody>
            </table>

            {/* Footer count */}
            <div style={{
              padding: '10px 16px',
              borderTop: '1px solid var(--border)',
              fontSize: 11,
              color: 'var(--muted)',
              textAlign: 'right',
            }}>
              Showing {filtered.length} of {orders.length} orders · {excludedOrders.length} excluded from chart
            </div>
          </div>
        )}

        {/* Legend */}
        {!loading && !error && totalReady > 0 && (
          <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
            {[
              { color: EVENT_COLORS['Snapshot'],     label: 'Snapshot — regular intraday capture' },
              { color: EVENT_COLORS['PO Received'],  label: 'PO Received — triggered by incoming stock' },
              { color: EVENT_COLORS['Inbound Sale'], label: 'Inbound Sale — new order checked on arrival' },
              ...(isPartial ? [] : [{ color: EVENT_COLORS['Reconciled'], label: 'Reconciled — backfilled from dispatch date' }]),
            ].map(({ color, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <span style={{ fontSize: 10, color: 'var(--muted)' }}>{label}</span>
              </div>
            ))}
          </div>
        )}

      </div>
    </main>
  );
};

export default DispatchDrilldown;
